import React, { useState } from "react";
import { MapPin, Clock, Shirt, ShieldCheck, CheckCircle2, Loader2 } from "lucide-react";
import Reveal from "../components/Reveal";
import api, { formatApiErrorDetail } from "../lib/api";
import { images } from "../data/content";
import { useSiteContent } from "../lib/cms";

export default function Visit() {
  const lodge = useSiteContent().block("visit_details");
  const [form, setForm] = useState({ name: "", email: "", phone: "", heard_from: "", message: "", consent: false });
  const [status, setStatus] = useState({ state: "idle", error: "" });

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      setStatus({ state: "error", error: "Please consent to us storing your details so we can reply." });
      return;
    }
    setStatus({ state: "loading", error: "" });
    try {
      await api.post("/inquiries", form);
      setStatus({ state: "success", error: "" });
      setForm({ name: "", email: "", phone: "", heard_from: "", message: "", consent: false });
    } catch (err) {
      setStatus({ state: "error", error: formatApiErrorDetail(err.response?.data?.detail) || err.message });
    }
  };

  return (
    <div data-testid="visit-page">
      <section className="relative flex min-h-[45vh] items-end">
        <img src={images.templeInterior} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="hero-overlay absolute inset-0" />
        <div className="container-lodge relative z-10 pb-14 pt-28">
          <Reveal>
            <p className="eyebrow">Begin Your Inquiry</p>
            <h1 className="display-h1 mt-4 max-w-3xl text-parchment">The first step is always yours.</h1>
          </Reveal>
        </div>
      </section>

      <section className="bg-paper-texture bg-parchment py-24">
        <div className="container-lodge grid gap-14 lg:grid-cols-5">
          {/* FORM */}
          <div className="lg:col-span-3">
            <Reveal>
              <p className="max-w-xl font-display text-2xl leading-relaxed text-sapphire-deep">
                "Freemasonry does not solicit its membership. If you are curious, we would be glad to
                hear from you — and we respond to every genuine inquiry within five business days."
              </p>
            </Reveal>

            <Reveal delay={0.1}>
              {status.state === "success" ? (
                <div
                  className="mt-10 rounded-2xl border border-emerald/30 bg-emerald/5 p-10 text-center"
                  data-testid="inquiry-success"
                >
                  <CheckCircle2 size={44} className="mx-auto text-emerald" />
                  <h3 className="mt-4 font-display text-2xl font-semibold text-sapphire-deep">Thank you.</h3>
                  <p className="mt-2 font-sans text-charcoal/75">
                    Your inquiry has been received. Our Lodge Secretary will be in touch shortly.
                  </p>
                  <button
                    className="btn-dark mt-6"
                    onClick={() => setStatus({ state: "idle", error: "" })}
                    data-testid="inquiry-reset"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="mt-10 space-y-5" data-testid="inquiry-form">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <Field label="Name" required>
                      <input required value={form.name} onChange={update("name")} className="input-lodge" data-testid="inquiry-name" />
                    </Field>
                    <Field label="Email" required>
                      <input type="email" required value={form.email} onChange={update("email")} className="input-lodge" data-testid="inquiry-email" />
                    </Field>
                  </div>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <Field label="Phone">
                      <input value={form.phone} onChange={update("phone")} className="input-lodge" data-testid="inquiry-phone" />
                    </Field>
                    <Field label="How did you hear about us?">
                      <input value={form.heard_from} onChange={update("heard_from")} className="input-lodge" data-testid="inquiry-heard" />
                    </Field>
                  </div>
                  <Field label="Your message" required>
                    <textarea required rows={5} value={form.message} onChange={update("message")} className="input-lodge resize-none" data-testid="inquiry-message" />
                  </Field>

                  <label className="flex items-start gap-3 font-sans text-sm text-charcoal/75">
                    <input type="checkbox" checked={form.consent} onChange={update("consent")} className="mt-1 h-4 w-4 accent-emerald" data-testid="inquiry-consent" />
                    I consent to the Lodge storing the information I have submitted so they can respond to my inquiry.
                  </label>

                  {status.error && <p className="font-sans text-sm text-garnet" data-testid="inquiry-error">{status.error}</p>}

                  <button type="submit" disabled={status.state === "loading"} className="btn-dark" data-testid="inquiry-submit">
                    {status.state === "loading" ? (
                      <>
                        <Loader2 size={16} className="animate-spin" /> Sending…
                      </>
                    ) : (
                      "Send Inquiry"
                    )}
                  </button>
                </form>
              )}
            </Reveal>
          </div>

          {/* VISITING BRETHREN + DETAILS */}
          <div className="lg:col-span-2">
            <Reveal delay={0.15}>
              <div className="rounded-2xl border border-sapphire-deep/10 bg-sapphire-deep p-8 text-parchment shadow-heritage">
                <h3 className="font-display text-2xl font-semibold text-gold">Visiting Brethren</h3>
                <ul className="mt-5 space-y-4 font-sans text-sm text-parchment/85">
                  <li className="flex gap-3"><Clock size={18} className="shrink-0 text-gold" /> {lodge.meeting}. Tyling at {lodge.tyling}.</li>
                  <li className="flex gap-3"><Shirt size={18} className="shrink-0 text-gold" /> Dress: {lodge.dress}.</li>
                  <li className="flex gap-3"><ShieldCheck size={18} className="shrink-0 text-gold" /> A Grand Lodge Certificate is required and every visitor will be proved.</li>
                  <li className="flex gap-3"><MapPin size={18} className="shrink-0 text-gold" /> {lodge.address}</li>
                </ul>
                <p className="mt-6 border-t border-white/10 pt-5 font-sans text-sm text-parchment/70">
                  Please contact the Lodge Secretary at{" "}
                  <a href={`mailto:${lodge.emailSecretary}`} className="text-gold underline underline-offset-2">
                    {lodge.emailSecretary}
                  </a>
                  .
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.25}>
              <div className="mt-6 overflow-hidden rounded-2xl border border-sapphire-deep/10 shadow-sm">
                <iframe
                  title="Freemasons' Hall, Coleman Street, Singapore"
                  src={`https://www.google.com/maps?q=${encodeURIComponent(lodge.address || "")}&output=embed`}
                  className="h-64 w-full"
                  loading="lazy"
                />
              </div>
              <div className="mt-4 rounded-xl bg-white p-5 font-sans text-sm text-charcoal/75">
                <p className="font-semibold text-sapphire-deep">Getting here</p>
                <p className="mt-1"><strong>MRT:</strong> City Hall (400 m) or Clarke Quay (500 m).</p>
                <p><strong>Bus:</strong> Stop 04142 / 04149 on Hill Street, by the Armenian Church.</p>
                <p><strong>Taxi:</strong> Next to the Central Fire Station on Hill Street, near Fort Canning Park.</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="mb-2 block font-sans text-xs font-semibold uppercase tracking-widest text-stonegrey">
        {label} {required && <span className="text-garnet">*</span>}
      </span>
      {children}
    </label>
  );
}
